
import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Instagram, Music, Globe, Youtube, Plus, Flame, Trophy } from 'lucide-react';
import ProfileAvatar from './ProfileAvatar';
import AccountStats from './AccountStats';
import { useToast } from '@/hooks/use-toast';
import { UserProfile } from '@/types';

interface ProfileSidebarProps {
  name: string;
  role: string;
  genres: string[];
  avatarSrc: string;
  userProfile?: UserProfile;
}

const ProfileSidebar: React.FC<ProfileSidebarProps> = ({ 
  name, 
  role, 
  genres, 
  avatarSrc,
  userProfile 
}) => {
  const { toast } = useToast();
  
  const handleSocialClick = (platform: string) => {
    toast({
      title: `${platform} profile`,
      description: `Opening ${platform} profile in a new tab`,
    });
  };
  
  // Streak information (use default if not available)
  const streakCount = userProfile?.dailyStreak?.currentStreak || 3;
  const hasStreakToday = userProfile?.dailyStreak?.lastLoginDate ? 
    new Date(userProfile.dailyStreak.lastLoginDate).toDateString() === new Date().toDateString() : 
    true;
  
  return (
    <div className="space-y-6">
      <Card className="neo-card">
        <CardContent className="p-6 text-center space-y-4">
          <div className="relative">
            <ProfileAvatar 
              imageSrc={avatarSrc}
              alt={name}
              fallback="AT"
            />
            
            {/* Enhanced Streak indicator */}
            {streakCount > 0 && (
              <Link to="/gamification" className="absolute -top-2 -right-2 group">
                <div className="relative flex items-center justify-center">
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center 
                    border-2 ${hasStreakToday ? 'border-neon-purple' : 'border-orange-400'} 
                    ${hasStreakToday ? 'bg-black shadow-neon-purple' : 'bg-black shadow-orange-400'} 
                    hover:scale-110 transition-transform
                  `}>
                    <Flame className={`h-4 w-4 ${hasStreakToday ? 'text-neon-purple' : 'text-orange-400'}`} />
                  </div>
                  <span className="absolute -top-2 -right-2 bg-black text-xs rounded-full h-5 w-5 flex items-center justify-center border border-white/20">
                    {streakCount}
                  </span>
                  <span className="absolute opacity-0 group-hover:opacity-100 bottom-full mb-2 left-1/2 -translate-x-1/2 bg-black/90 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap transition-opacity">
                    {streakCount} day streak! Click to view rewards
                  </span>
                </div>
              </Link>
            )}
          </div>
          
          {/* Gamification button */}
          <Link to="/gamification" className="block mt-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full gap-2 text-purple-400 border-purple-400/30 hover:bg-purple-500/10 hover:border-purple-500/50"
            >
              <Trophy className="h-3 w-3" />
              <span>View Progress & Rewards</span>
            </Button>
          </Link>
          
          <div>
            <h2 className="text-xl font-bold">{name}</h2>
            <p className="text-muted-foreground">{role}</p>
          </div>
          
          <div className="flex justify-center flex-wrap gap-2">
            {genres.map((genre, index) => (
              <Badge key={index} variant="outline">{genre}</Badge>
            ))}
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 text-xs text-muted-foreground"
              onClick={() => {
                toast({
                  title: "Add genre",
                  description: "Genre management feature coming soon",
                });
              }}
            >
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>
          
          <div className="pt-2 flex justify-center gap-1">
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full h-8 w-8 text-muted-foreground"
              onClick={() => handleSocialClick("Instagram")}
            >
              <Instagram className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full h-8 w-8 text-muted-foreground"
              onClick={() => handleSocialClick("Music")}
            >
              <Music className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full h-8 w-8 text-muted-foreground"
              onClick={() => handleSocialClick("YouTube")}
            >
              <Youtube className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full h-8 w-8 text-muted-foreground"
              onClick={() => handleSocialClick("Website")}
            >
              <Globe className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
      
      <AccountStats userProfile={userProfile} />
    </div>
  );
};

export default ProfileSidebar;
